// src/encounter/lab-add-dialog.tsx
// Self-contained lab-order picker for the A/P canvas. Like the imaging dialog, it owns its OWN
// search: it calls useLabSearch(providerId, query) internally for the lab catalog, and — once a lab
// is picked — useLabSubTests(code, system, gender) for the value-set panel members. It composes the
// DS CodeSearchField (coded lab pick) + the TESTS list (LabTestsList, the same component for the
// v2 and legacy flows) + a TextArea (free-text note) + Save, and on confirm calls
// onConfirm({ planOrder, payload }) with the to-lab-order mapping.
//
// Centered DS Modal (matches procedure/referral/vaccine add dialogs) — the canvas opens it when the
// Dx's "+ Lab" is tapped or a lab is dropped, so the user always sees the form as an overlay.
//
// orderId: derived from the picked code + idSuffix (NOT Math.random/Date.now — keeps the id stable
// for a given pick and lets the host disambiguate the same panel attached to >1 Dx via the suffix).
//
// Select-all default (PROD-2623): a picked panel starts with EVERY member selected, once the value-set
// members load. An empty member list used to save as a valid order (805 of 1,033 IHL orders on dev had
// an empty snapshot — nobody could tell "whole panel" from "nothing"). A deselected test shows as a
// left-out row (PROD-2640), and Save is blocked when a panel that HAS members ends up with none
// selected. A lab with no members still saves, shown as one locked "Tests (1/1)" row. Edit mode keeps its seeded selection (PROD-1433).
//
// Omitted members (PROD-2632): on confirm the members the clinician DESELECTED go out as
// payload.omittedTests (payload.subTests stays the selected ones). Computed only when the members
// loaded; otherwise the server's list (initialOmittedTests) is passed through unchanged, because the
// BE cancels a still-Ordered member that is in neither list — dropping it would cancel real rows.
//
// Performing lab (PROD-2653): "Performing lab: <vendor>" on the right of the LABS header row, with
// a Change link that lists the clinic's ACTIVE vendors. A new order defaults per lib/lab-vendor.ts
// pickDefaultVendor (catalogue labVendorId, else the clinic's only vendor, else none — shown as
// "Not set", which never blocks Save). Edit mode reopens on the saved vendor and applies no default.
//
// Catalogue v2 (PROD-2640): when the v2 API has a PUBLISHED panel for the picked code/system
// (services/use-lab-catalogue.ts), the popup follows the design (canvas artboards 6-8): a kind chip
// in the header, the panel's code line, the specimen / "Bills as" chip row, and the TESTS list box
// (LabTestsList). CPT and lab panels are LOCKED (ordered as one panel); a house panel's tests can be
// left out. Rflx tests are listed with the lab's rule and never sent. A test (or nested panel)
// another lab on the plan already has shows as one "Already ordered in X" row and goes out in
// `alreadyOrderedTests` (Patient #1001, P-B7), in neither subTests nor omittedTests; a reopen seeds
// those tests back from the saved list. The vendor defaults from the panel's labVendorId, and the
// order carries panelDefinitionId + panelVersion.
//
// Bill to (PROD-2640, Patient #1000): Patient / Clinic, defaulting to Clinic for every vendor
// (Andrew's ruling), sent as `billTo` on both flows; an edit reopens on the saved value.
// Anything else (503 before the scripts run, 404, no match, still checking) is the LabOrderValueSet
// flow above, unchanged: same chips, same seeding, same payload. An edit reopens v2 only when the
// saved order names a panel version; a legacy order stays on the legacy flow.
//
// PROD-2849, the agreed lab model (canvas boards 6-9): `LabAddDialog` below renders
// lab-add-dialog-v3.tsx when Scrips.Master's A&P lab search (`GET order-search`, PROD-2839) answers
// here. When it returns 404 LAB_ORDER_SEARCH_V3_OFF / LAB_CATALOGUE_V2_READS_OFF (the Master flags,
// default off), a 503 or any other failure, it renders this popup (`LegacyLabAddDialog`) unchanged.
// Order-set authoring and the edit of an order placed before this session always use this popup.
import { useMemo, useState } from 'react';
import { CodeSearchField, Button, type CodeSearchResult } from '@scripsteam/scrips-signal-ds';
import { useLabSearch, useLabSubTests } from './services/use-lab-search';
import { useLabVendors } from './services/use-lab-vendors';
import { useLabCataloguePanel } from './services/use-lab-catalogue';
import { LabTestsList, type LabTestsListProps, type LabTestsListRow } from './lab-tests-list';
import { LabBillTo, LabChipRow, LabHeaderAside } from './lab-add-parts';
import { LAB_BILL_TO_DEFAULT, type LabBillToValue } from './lib/lab-bill-to';
import {
  HOUSE_HINT,
  LOCKED_HINT,
  SHARED_HINT,
  billsAsChip,
  billsAsComponents,
  groupPanelMembers,
  isLockedKind,
  keysCoveredByAlreadyOrdered,
  kindLabel,
  kindTone,
  panelCodeLine,
  selectableRows,
  specimensOf,
  testsHeader,
  toTestViews,
  type OtherLabOnPlan,
} from './lib/lab-catalogue';
import { catalogueVendorIdOf, pickDefaultVendor, toLabVendorRef } from './lib/lab-vendor';
import { toLabOrder, type LabOrderResult } from './lib/to-lab-order';
import { OrderDialogShell } from './order-dialog-shell';
import type { DiagnosisOption } from './diagnosis-multi-select';
import type { LabAoeAnswer, LabOrderSearchRef, LabVendorRef, OrderCoding } from './lib/order-payload';
import { LabAddDialogV3 } from './lab-add-dialog-v3';
import { useLabOrderSearchMode } from './services/use-lab-order-search';

/** The confirm payload, widened with the diagnoses the clinician linked (PROD-1801). */
export interface LabConfirm extends LabOrderResult {
  /** Every linked diagnosis, in canvas order — dxIds[0] is the nesting parent. */
  dxIds: string[];
}

export interface LabAddDialogProps {
  /** The encounter's diagnoses, for the DIAGNOSIS block (PROD-1801). */
  diagnoses: DiagnosisOption[];
  /** Pre-ticked diagnoses — the canvas seeds the Dx the picker was opened from. */
  initialDxIds?: readonly string[];
  /** Practitioner id — scopes the provider lab catalog. */
  providerId: string | null | undefined;
  /** Patient gender — narrows sex-specific value-set panels (optional). */
  gender?: string;
  /** Stable suffix folded into the generated orderId (the host increments it per attachment). */
  idSuffix?: string | number;
  /**
   * Pre-selected lab (seeded from a dragged panel item — PROD-1419). When present the study is
   * already picked (sub-tests + note show immediately), so a dragged CBC needs NO manual re-search.
   * Flutter: assessment_plan_screen.dart:130 addItemInApSection opens the Add dialog with the item.
   */
  initialItem?: CodeSearchResult;
  /** Editing an existing order (PROD-1433): pre-select these sub-tests + note. */
  initialSubTests?: OrderCoding[];
  /**
   * Editing an existing order (PROD-2632): the members the server says were deselected. `undefined`
   * = unknown (server null). Re-sent as-is when the panel's members can't be loaded.
   */
  initialOmittedTests?: OrderCoding[];
  initialNote?: string;
  /** Editing an existing order (PROD-2653): the saved performing lab vendor, if any. */
  initialVendor?: LabVendorRef;
  /**
   * Editing an order placed on a catalogue v2 panel (PROD-2640): the saved version, reopened as-is.
   * Absent = a legacy order (the LabOrderValueSet flow).
   */
  initialPanelDefinitionId?: string;
  initialPanelVersion?: number;
  /** Editing an existing order (PROD-2640, Patient #1000): the saved Bill to. Absent = Clinic. */
  initialBillTo?: LabBillToValue;
  /**
   * Editing an existing order (PROD-2640, Patient #1001): the members saved as already ordered by
   * another lab on the plan. They are not in initialSubTests, so the v2 seed adds them back.
   */
  initialAlreadyOrderedTests?: OrderCoding[];
  /** The other labs already on this plan, for the "Already ordered in <panel>" hint (PROD-2640). */
  otherLabs?: readonly OtherLabOnPlan[];
  /** Editing an existing order — switches title/confirm copy to "Edit". */
  editing?: boolean;
  onConfirm: (order: LabConfirm) => void;
  /**
   * Destructive DELETE, bottom-right — the red trash glyph in the Figma frames. Editing only (the
   * host passes its existing order-removal callback); omitted, nothing renders.
   */
  onDelete?: () => void;
  onCancel: () => void;
  /**
   * AUTHORING an order set (PROD-2714): the same form with no encounter. The DIAGNOSIS block and the
   * note are not shown (see OrderDialogShell `authoring`).
   */
  authoring?: boolean;
  /**
   * PROD-2849: editing an order placed from the order-search popup in this session (the payload's
   * `orderSearch`). Absent = a legacy order, which reopens in the legacy popup.
   */
  initialOrderSearch?: LabOrderSearchRef;
  /** PROD-2849: the saved answers to the lab's order questions. */
  initialAoeAnswers?: LabAoeAnswer[];
}

/**
 * The Add lab popup. On Master's order-search when it answers (PROD-2849, the agreed lab model);
 * otherwise the current popup, unchanged. Until the one probe answers (once per session, a few hundred
 * milliseconds on the first open) the current popup is shown, so nothing waits on it.
 */
export function LabAddDialog(props: LabAddDialogProps) {
  const eligible = !props.authoring && (!props.editing || !!props.initialOrderSearch);
  const mode = useLabOrderSearchMode(eligible);
  if (!eligible || mode !== 'v3') return <LegacyLabAddDialog {...props} />;
  return (
    <LabAddDialogV3
      diagnoses={props.diagnoses}
      initialDxIds={props.initialDxIds}
      idSuffix={props.idSuffix}
      initialItem={props.initialItem}
      initialSubTests={props.initialSubTests}
      initialNote={props.initialNote}
      initialVendor={props.initialVendor}
      initialOrderSearch={props.initialOrderSearch}
      initialAoeAnswers={props.initialAoeAnswers}
      otherLabs={props.otherLabs}
      editing={props.editing}
      onConfirm={props.onConfirm}
      onDelete={props.onDelete}
      onCancel={props.onCancel}
    />
  );
}

const SUBTEST_HEADER = 'flex items-center justify-between';
const HINT_ERROR = 'm-0 mt-2 text-size-xs text-status-error';
// The no-results box (canvas artboard 9 "No results"): bordered, centred, 24/12 padding.
const EMPTY_BOX = 'rounded-md border border-solid border-border-subtle px-3 py-6 text-center';
const EMPTY_TITLE = 'text-size-sm font-semibold text-text-primary';
const EMPTY_BODY = 'mt-1 text-size-sm leading-meta text-text-muted';
// TODO(DS gap): the DS CodeSearchField (2.5.2) always prints its own "No matching codes" line under
// the field, and has no prop to turn it off. The design shows only the box below, so until the DS
// exposes one, both show. No workaround here: the PA guard forbids arbitrary selector classes.

/** "NRL", "NRL and Labcorp", "NRL, Labcorp and Unilabs". */
function joinNames(names: readonly string[]): string {
  if (names.length <= 1) return names[0] ?? '';
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

/** A legacy coding's LOINC: its code when the coding system is LOINC, else none ("—"). */
function loincOf(c: { code?: string; system?: string }): string | undefined {
  return c.code && /loinc/i.test(c.system ?? '') ? c.code : undefined;
}

/**
 * A single test's code line (9.4 / 9.6). A known standard system names itself with the code
 * ("LOINC 4548-4", "CPT 83036", like the order card); "<code> · house code" only for a test with no
 * system or a house (IHL) system (PROD-2827).
 */
const STANDARD_SINGLE_TEST_SYSTEMS: ReadonlyArray<readonly [RegExp, string]> = [
  [/loinc/i, 'LOINC'],
  [/\bcpt\b|ama-assn/i, 'CPT'],
  [/snomed/i, 'SNOMED'],
];
function singleTestCodeLine(c: { code: string; system?: string }): string {
  const system = STANDARD_SINGLE_TEST_SYSTEMS.find(([re]) => re.test(c.system ?? ''))?.[1];
  return system ? `${system} ${c.code}` : `${c.code} · house code`;
}

/** Stable key for a sub-test coding (code, else display). */
function keyOf(t: OrderCoding): string {
  return t.code || t.displayName;
}

/** Stable key for a picked lab — which lab the select-all seed was applied to. */
function labKeyOf(r: CodeSearchResult | undefined): string | null {
  return r ? r.code || r.id : null;
}

function LegacyLabAddDialog({ gender, diagnoses, initialDxIds, idSuffix = '0', initialItem, initialSubTests, initialOmittedTests, initialNote, initialVendor, initialPanelDefinitionId, initialPanelVersion, initialBillTo, initialAlreadyOrderedTests, otherLabs, editing, onConfirm, onCancel, onDelete, authoring = false }: LabAddDialogProps) {
  const [query, setQuery] = useState('');
  const [dxIds, setDxIds] = useState<string[]>(() => [...(initialDxIds ?? [])]);
  const [selected, setSelected] = useState<CodeSearchResult | undefined>(initialItem);
  // Pre-select the saved sub-tests on edit (matched by key against the value-set panel once it loads).
  const [picked, setPicked] = useState<Set<string>>(() => new Set((initialSubTests ?? []).map(keyOf)));
  const [note, setNote] = useState(initialNote ?? '');
  // The lab whose members have already been seeded into `picked` (PROD-2623). Edit mode starts as
  // "already seeded" so the saved selection (PROD-1433) is never overridden by select-all; a fresh
  // pick (or a drag seed that isn't an edit) starts unseeded and gets ALL members once they load.
  const [seededFor, setSeededFor] = useState<string | null>(() => (editing ? labKeyOf(initialItem) : null));

  // Search the GLOBAL/master catalog (providerId omitted) so it matches the A/P panel's source —
  // a lab dragged from the panel is findable here even when provider favourites are empty (PROD-1421;
  // Codex #145). Per-item seeding + provider Favourites are PROD-1419 / Phase C.
  const search = useLabSearch(undefined, query);
  const subTests = useLabSubTests(selected?.code ?? null, selected?.system ?? null, gender);

  const available = useMemo(() => subTests.data ?? [], [subTests.data]);

  // PROD-2640 catalogue v2. A new order looks the picked code up; an edit reopens the saved version
  // (and a legacy edit does not look anything up, so it stays exactly as before).
  const catalogue = useLabCataloguePanel(
    editing
      ? { panelId: initialPanelDefinitionId, enabled: !!initialPanelDefinitionId }
      : { code: selected?.code, system: selected?.system },
  );
  const v2 = selected ? (catalogue.data ?? null) : null;
  const v2Groups = useMemo(() => (v2 ? groupPanelMembers(v2) : []), [v2]);
  const v2Selectable = useMemo(() => selectableRows(v2Groups), [v2Groups]);
  const [v2Picked, setV2Picked] = useState<Set<string>>(() => new Set());
  const [v2SeededFor, setV2SeededFor] = useState<string | null>(null);

  // PROD-2653 performing lab. `vendorPick` is the clinician's explicit choice (null = "Not set");
  // until they make one, a NEW order shows the default and an edit shows what was saved.
  const vendors = useLabVendors();
  const [vendorPick, setVendorPick] = useState<{ ref: LabVendorRef | undefined } | null>(null);
  // BILL TO (6.13 / 7.10): Clinic for every vendor unless the clinician overrides it; an edit
  // reopens on the saved value (an order saved before Bill to existed shows the default).
  const [billTo, setBillTo] = useState<LabBillToValue>(initialBillTo ?? LAB_BILL_TO_DEFAULT);
  const defaultVendor = useMemo(() => {
    const v = pickDefaultVendor(vendors.data, v2?.labVendorId ?? catalogueVendorIdOf(selected));
    return v ? toLabVendorRef(v) : undefined;
  }, [vendors.data, selected, v2]);
  const vendor: LabVendorRef | undefined = vendorPick
    ? vendorPick.ref
    : editing
      ? initialVendor
      : defaultVendor;
  const vendorChoices = useMemo(
    () => (vendors.data ?? []).map((v) => ({ id: v.id, name: v.name })),
    [vendors.data],
  );

  function handleVendorChange(id: string) {
    const hit = (vendors.data ?? []).find((v) => v.id === id);
    if (!hit) return;
    // Keep the order's persisted OrderLabs row ids, so a changed vendor updates that row.
    setVendorPick({
      ref: {
        ...toLabVendorRef(hit),
        ...(initialVendor?.orderLabsId ? { orderLabsId: initialVendor.orderLabsId } : {}),
        ...(initialVendor?.serviceOrderId ? { serviceOrderId: initialVendor.serviceOrderId } : {}),
      },
    });
  }

  // Seed select-all when the members arrive for a newly picked lab. This is React's documented
  // "adjust state while rendering" pattern (react.dev: "Storing information from previous renders"),
  // not an effect: the setters are guarded by seededFor !== labKey, so React discards this render and
  // re-renders once with the seed applied — before paint, no effect loop, no StrictMode warning (a
  // StrictMode test covers it). An effect would paint the chips unselected (and Save enabled) for a
  // frame first. Runs once per pick: after seeding, seededFor === labKey, so the user's own toggles /
  // Reset are never overwritten. The host unmounts the dialog on close, so every open starts fresh.
  const labKey = labKeyOf(selected);
  if (labKey && seededFor !== labKey && subTests.data) {
    setSeededFor(labKey);
    setPicked(new Set(subTests.data.map(keyOf)));
  }
  // v2 seed, same render-time pattern, once per panel version: a new order starts with every R/O
  // test; an edit starts with what was saved, plus the tests its saved already-ordered list covers
  // (the snapshot leaves those out). While their owner is on the plan they show as "Already
  // ordered"; if it is gone, they are ordered here, which is Patient's own fallback (#1001).
  if (v2 && v2SeededFor !== v2.id) {
    setV2SeededFor(v2.id);
    setV2Picked(
      editing
        ? new Set([
            ...(initialSubTests ?? []).map(keyOf),
            ...keysCoveredByAlreadyOrdered(v2Groups, initialAlreadyOrderedTests),
          ])
        : new Set(v2Selectable.map((r) => r.key)),
    );
  }
  const v2SelectedCount = v2Selectable.filter((r) => v2Picked.has(r.key)).length;
  const v2None = !!v2 && v2Selectable.length > 0 && v2SelectedCount === 0;
  const v2Locked = !!v2 && isLockedKind(v2.kind);
  const v2Views = useMemo(() => toTestViews(v2Groups, otherLabs), [v2Groups, otherLabs]);
  const v2Tests = v2 ? buildV2Tests() : null;

  /** The TESTS list for a v2 panel: header counts, rows, the hint and the house billing box. */
  function buildV2Tests(): LabTestsListProps {
    const { views, alreadyKeys } = v2Views;
    const fresh = v2Selectable.filter((r) => !alreadyKeys.has(r.key));
    const freshPicked = fresh.filter((r) => v2Picked.has(r.key));
    const already = views.filter((v) => v.state === 'already').length;
    const rows: LabTestsListRow[] = views.map((v) =>
      v.state === 'test' ? { ...v, checked: v2Picked.has(v.key), locked: v2Locked } : v,
    );
    return {
      header: testsHeader({
        selected: v2SelectedCount,
        selectable: v2Selectable.length,
        newSelected: freshPicked.length,
        already,
        reflex: views.filter((v) => v.state === 'reflex').length,
      }),
      rows,
      onToggle: toggleV2,
      error: v2None ? 'Select at least one test' : null,
      hint: already
        ? { icon: 'layers', text: SHARED_HINT }
        : v2Locked
          ? { icon: 'lock', text: LOCKED_HINT }
          : { icon: 'info', text: HOUSE_HINT },
      billing: v2Locked ? null : billsAsComponents(freshPicked, fresh.length - freshPicked.length),
    };
  }

  // Members still loading for a pick that hasn't been seeded — hold Save so an order can't slip
  // through with an empty member list before select-all lands.
  const awaitingSeed = !!labKey && seededFor !== labKey && !!subTests.isLoading;
  // The members query FAILED for an unseeded pick: we don't know the panel's members, so an order
  // saved now would carry an empty list — the exact ambiguity this ticket removes. Hold Save and
  // offer a retry. (Edit mode is already seeded, so the PROD-1433 edit path is unaffected.)
  const membersFailed = !!labKey && seededFor !== labKey && !subTests.data && !!subTests.isError;
  // Editing a LEGACY order saved with an empty member list (805 of 1,033 IHL orders on dev). Its
  // meaning is unknown, so we neither rewrite it to "all" nor hard-block the edit (a clinician
  // fixing the note must not be stopped): zero stays saveable here, with a prompt to pick the tests.
  const legacyEmptyEdit = !!editing && (initialSubTests ?? []).length === 0;

  const selectedCount = available.filter((t) => picked.has(keyOf(t))).length;
  const availableKeys = new Set(available.map(keyOf));
  // On edit, saved sub-tests that are still selected but ABSENT from the value-set panel.
  const seededMissing = (initialSubTests ?? []).filter((t) => picked.has(keyOf(t)) && !availableKeys.has(keyOf(t)));
  // A panel that HAS members must keep at least one selected; a lab with no members still saves.
  const noneSelected = available.length > 0 && selectedCount === 0 && seededMissing.length === 0;
  const emptyPanel = noneSelected && !legacyEmptyEdit;

  // The legacy gates apply only on the legacy flow; v2 has its own (at least one R/O test).
  const blocked = v2 ? v2None : emptyPanel || awaitingSeed || membersFailed;

  function handlePickLab(r: CodeSearchResult) {
    setSelected(r);
    setPicked(new Set()); // cleared until the new lab's members load, then seeded with all of them
    setSeededFor(null);
    setV2SeededFor(null);
  }

  function toggleV2(key: string) {
    // CPT and lab panels are locked; reflex and already-ordered rows have no checkbox.
    if (v2Locked || v2Views.alreadyKeys.has(key)) return;
    if (!v2Selectable.some((r) => r.key === key)) return;
    setV2Picked((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  function toggle(t: OrderCoding) {
    const k = keyOf(t);
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });
  }


  function handleConfirm() {
    if (!selected) return;
    if (blocked) return;
    const orderId = `lab-${selected.code ?? selected.id}-${idSuffix}`;
    if (v2) {
      // Snapshot for the old readers: the selected R/O tests, and the deselected ones as omitted.
      // Reflex tests are in neither list: they are not ordered and not left out. Already-ordered
      // tests are in neither list either (Patient #1001): they go out as alreadyOrderedTests, one
      // entry per "Already ordered" row (a nested panel is one entry covering its tests).
      const { alreadyKeys, alreadyOrdered } = v2Views;
      const fresh = v2Selectable.filter((r) => !alreadyKeys.has(r.key));
      const chosenRows = fresh.filter((r) => v2Picked.has(r.key));
      const chosenV2 = chosenRows.map((r) => r.coding);
      const omittedV2 = fresh.filter((r) => !v2Picked.has(r.key)).map((r) => r.coding);
      // The plan row's detail line (6.18 / 8.8): the ordered tests' specimen, and "ordered as one
      // panel" for a locked (CPT or lab) panel. Display only; never sent.
      const specimens = [...new Set(chosenRows.map((r) => r.specimenName).filter(Boolean))];
      onConfirm({
        ...toLabOrder(selected, chosenV2, orderId, note, omittedV2, vendor, { id: v2.id, version: v2.version }, {
          specimen: specimens.join(', ') || undefined,
          orderedAsOnePanel: v2Locked,
        }, { billTo, alreadyOrderedTests: alreadyOrdered }),
        dxIds,
      });
      return;
    }
    const chosen = available.filter((t) => picked.has(keyOf(t)));
    // On edit, preserve saved sub-tests that are still selected but ABSENT from the value-set panel
    // (seededMissing) — e.g. the user saved before useLabSubTests resolved, or the catalog no longer
    // returns them. Without this, an ordinary edit-confirm would silently drop them (Codex Medium).
    const subTestsOut = [...chosen, ...seededMissing];
    // An edit of a v2 order whose version can't be loaded now keeps the saved version link, as
    // long as it is still the same lab; a legacy pick carries none.
    const keptPanel =
      editing && initialPanelDefinitionId && labKey === labKeyOf(initialItem)
        ? { id: initialPanelDefinitionId, version: initialPanelVersion }
        : undefined;
    // The legacy flow has no catalogue to tell which members another lab holds. An edit that fell
    // back here (its v2 version could not load) re-sends the saved already-ordered list unchanged.
    const keptAlready = serverAlready();
    onConfirm({
      ...toLabOrder(selected, subTestsOut, orderId, note, omittedFor(subTestsOut), vendor, keptPanel, undefined, {
        billTo,
        ...(keptAlready ? { alreadyOrderedTests: keptAlready } : {}),
      }),
      dxIds,
    });
  }

  /** The saved already-ordered list, only while the dialog still has the lab it was opened with. */
  function serverAlready(): OrderCoding[] | undefined {
    return labKey === labKeyOf(initialItem) && initialAlreadyOrderedTests?.length
      ? initialAlreadyOrderedTests
      : undefined;
  }

  // PROD-2632: the deselected members = loaded panel members − picked. Server-omitted members the
  // catalog no longer returns are kept (mirrors seededMissing), so a re-save doesn't cancel them.
  // Members never loaded → pass the server's list through untouched (undefined stays undefined).
  // Nothing selected at all (only a LEGACY empty edit can save that) → also pass through: marking the
  // whole panel omitted would rewrite an order whose meaning is unknown.
  // The server's list only describes the lab the dialog was opened with — a different pick drops it.
  // A saved already-ordered list (only possible on an edit that fell back from v2) also passes the
  // server's list through: the legacy members can't tell an already-ordered test (or a nested
  // panel's tests) from a deselected one, and calling it omitted would make Patient omit it.
  function omittedFor(chosen: OrderCoding[]): OrderCoding[] | undefined {
    const serverOmitted = labKey === labKeyOf(initialItem) ? initialOmittedTests : undefined;
    if (!subTests.data || chosen.length === 0 || serverAlready()) return serverOmitted;
    const chosenKeys = new Set(chosen.map(keyOf));
    const deselected = available.filter((t) => !chosenKeys.has(keyOf(t)));
    const omittedMissing = (serverOmitted ?? []).filter(
      (t) => !availableKeys.has(keyOf(t)) && !picked.has(keyOf(t)),
    );
    return [...deselected, ...omittedMissing];
  }

  // The legacy (LabOrderValueSet) flow on the same TESTS list as v2 (PROD-2640): tickable rows,
  // no kind chip (the legacy read has no kind). A lab with NO members is a single test: one locked
  // row for the lab itself, "Tests (1/1)", with the "Test" chip (canvas artboard 9, 9.3-9.6).
  const legacySingle = !v2 && !!selected && !!subTests.data && available.length === 0 && !membersFailed;
  const legacyTests: LabTestsListProps | null =
    v2 || !selected
      ? null
      : legacySingle
        ? {
            header: 'Tests (1/1)',
            rows: [
              {
                key: labKey ?? selected.display,
                name: selected.display,
                loinc: loincOf(selected),
                state: 'test',
                checked: true,
                locked: true,
              },
            ],
            onToggle: () => {},
          }
        : available.length > 0
          ? {
              header: `Tests (${selectedCount}/${available.length})`,
              rows: available.map((t) => ({
                key: keyOf(t),
                name: t.displayName,
                loinc: loincOf(t),
                state: 'test' as const,
                checked: picked.has(keyOf(t)),
              })),
              onToggle: (key: string) => {
                const t = available.find((a) => keyOf(a) === key);
                if (t) toggle(t);
              },
              error: emptyPanel ? 'Select at least one test' : null,
              // No kind on a legacy order, so no house-panel "left out" sentence: strike + Undo only.
              leftOutNote: null,
              hint:
                noneSelected && legacyEmptyEdit
                  ? { icon: 'info', text: 'No tests recorded on this order · select the tests to include' }
                  : null,
            }
          : null;
  const itemCode = v2
    ? panelCodeLine(v2) || undefined
    : legacySingle && selected?.code
      ? singleTestCodeLine({ code: selected.code, system: selected.system })
      : selected?.code;

  // No results (canvas artboard 9, 9.1): the design's box, naming the clinic's active vendors.
  const q = query.trim();
  const noResults =
    !selected && !initialItem && q.length >= 2 && !search.isFetching && !!search.data && search.data.length === 0;
  const vendorNames = joinNames(vendorChoices.map((v) => v.name).filter(Boolean));
  const emptyBox = noResults ? (
    <div className={EMPTY_BOX} data-testid="lab-no-results">
      <div className={EMPTY_TITLE}>{`No lab tests match “${q}”`}</div>
      <div className={EMPTY_BODY}>
        {vendorNames
          ? `Search by name, lab code or LOINC. Only tests your clinic can order from ${vendorNames} are listed.`
          : 'Search by name, lab code or LOINC. Only tests your clinic can order are listed.'}
      </div>
    </div>
  ) : undefined;

  return (
    <OrderDialogShell
      authoring={authoring}
      title={editing ? 'Edit lab' : 'Add lab'}
      // The lab popup alone paints its title (Andrew's ruling, PROD-2640); other order dialogs don't.
      showTitle
      fieldLabel="Labs"
      itemName={selected?.display ?? 'No lab selected'}
      itemCode={itemCode}
      emptySlot={emptyBox}
      headerAside={
        selected ? (
          <LabHeaderAside
            kind={
              v2
                ? { label: kindLabel(v2.kind), tone: kindTone(v2.kind) }
                : legacySingle
                  ? { label: 'Test', tone: 'grey' }
                  : undefined
            }
            vendorName={vendor ? vendor.name || 'Saved lab' : undefined}
            vendors={vendorChoices}
            onPickVendor={handleVendorChange}
          />
        ) : undefined
      }
      subHeader={v2 ? <LabChipRow specimens={specimensOf(v2Groups)} billsAs={billsAsChip(v2)} /> : undefined}
      // The lab popup's DIAGNOSIS variant (no count, Select all, 3 columns, selector left).
      diagnosisVariant="select-all"
      diagnoses={diagnoses}
      selectedDxIds={dxIds}
      onDxChange={setDxIds}
      note={note}
      onNoteChange={setNote}
      // ONE SAVE LABEL across every A/P dialog (Andrew, 1 Aug). Flutter's shared add widget defaults to
      // S.of(context).save — "Save" — for all of them (base_ap_item_add_widget.dart:288-290).
      // The shell already blocks a save with zero diagnoses; this adds "a lab must be chosen".
      // PROD-2623: and a panel with members can't save with none of them selected (or before the
      // select-all seed has landed, or when the members failed to load).
      canSave={!!selected && !blocked}
      onSave={handleConfirm}
      onCancel={onCancel}
      onDelete={onDelete}
      // Seeded from a drag (initialItem) → the study is fixed, matching the frame's filled state and
      // Flutter's seeded labs_add_widget. Cold open ("+ Lab") keeps the search.
      searchSlot={
        initialItem ? undefined : (
          <CodeSearchField
            value={query}
            onChange={setQuery}
            results={search.data ?? []}
            loading={search.isFetching}
            onResultClick={handlePickLab}
            placeholder="Search lab orders…"
          />
        )
      }
    >
      {v2Tests && <LabTestsList {...v2Tests} />}
      {legacyTests && <LabTestsList {...legacyTests} />}
      {!v2 && membersFailed && (
        <div className={SUBTEST_HEADER}>
          <p className={HINT_ERROR}>Couldn&apos;t load this lab&apos;s tests</p>
          <Button variant="tertiary" size="sm" onClick={() => void subTests.refetch?.()}>
            Retry
          </Button>
        </div>
      )}
      {selected && (
        <LabBillTo vendorName={vendor?.name} value={billTo} onChange={setBillTo} />
      )}
    </OrderDialogShell>
  );
}
